import { useEffect, useRef, useState, type ReactNode } from 'react';
import Nav from './Nav';
import type { PageKey } from '../App';
import type { Session } from '../types/token';
import { useT } from '../i18n';

interface LayoutProps {
  current: PageKey;
  onNavigate: (page: PageKey) => void;
  session: Session;
  onLogout: () => void;
  /**
   * Optional key controlling the fade-in animation. Defaults to `current`.
   * Use a finer-grained value when a sub-page transition (e.g. work detail)
   * should also animate.
   */
  transitionKey?: string;
  children: ReactNode;
}

export default function Layout({
  current,
  onNavigate,
  session,
  onLogout,
  transitionKey,
  children,
}: LayoutProps) {
  const t = useT();
  const animKey = transitionKey ?? current;
  const footerRef = useRef<HTMLElement>(null);
  const [footerVisible, setFooterVisible] = useState(false);
  const releaseTimestamp = import.meta.env.VITE_RELEASE_TIMESTAMP ?? new Date().toISOString();
  const releaseTime = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: 'Asia/Shanghai',
  }).format(new Date(releaseTimestamp));

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => setFooterVisible(entry.isIntersecting),
      { threshold: 0.25 },
    );

    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Nav
        current={current}
        onNavigate={onNavigate}
        session={session}
        onLogout={onLogout}
      />
      <main className="flex-1 px-6 py-10 md:px-8 max-w-6xl w-full mx-auto">
        <div key={animKey} className="animate-fade-in">
          {children}
        </div>
      </main>
      <footer
        ref={footerRef}
        className={[
          'mx-auto w-[calc(100%-1.5rem)] max-w-7xl border-t border-neutral-200/70 px-6 py-6 text-xs font-mono text-coffee/60 md:w-[calc(100%-2.5rem)] md:px-8',
          footerVisible ? 'footer-dissolve-in' : 'opacity-0 translate-y-3 blur-sm',
        ].join(' ')}
      >
        {t('common.footer', { year: new Date().getFullYear(), releaseTime })}
      </footer>
    </div>
  );
}
